import { Box, alpha, makeStyles } from '@material-ui/core';
import 'utils/antDesignCss.css';
require('json-schema-editor-visual/dist/main.css');
import schemaEditor from 'json-schema-editor-visual/dist/main.js';
import { JSONSchema6 } from 'json-schema';
import useRouteBlocker from 'common/useBlocker';
import { useBlocker } from 'react-router-dom';
import { isEmpty, isEqual } from 'lodash';
import { eventEditorSchema } from 'pages/aggregates/utils/eventEditorSchema';
import { useCallback } from 'react';

interface Props {
  onChange: (schema: JSONSchema6) => void;
  schemaRef: { current: {} | JSONSchema6 };
  schemaValue: JSONSchema6;
  onSave: () => void;
}

const SchemaEditor = ({ onChange, schemaRef, schemaValue, onSave }: Props) => {
  const classes = useStyles();
  let actualSchema: JSONSchema6 = {};
  const option = { data: eventEditorSchema };
  const Editor = schemaEditor(option);

  const clearSchema = () => {
    schemaRef.current = {};
  };

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const isBlocked =
      currentLocation.pathname !== nextLocation.pathname && !isEqual(actualSchema, schemaValue);
    if (!isBlocked) {
      clearSchema();
    }
    return isBlocked;
  });

  useRouteBlocker({ blocker, onSave, onDiscard: clearSchema });

  const onUpdate = useCallback(
    (schema: JSONSchema6) => {
      if (blocker.state !== 'blocked') {
        schemaRef.current = schema;
        onChange(schema);
      }
    },
    [blocker.state, schemaRef, onChange]
  );

  const schemaFinalData = !isEmpty(schemaRef.current) ? schemaRef.current : schemaValue;
  const schemaData = JSON.stringify(schemaFinalData);

  return (
    <Box className={classes.schemaEditorContainer}>
      <Editor
        onChange={(schema: string) => {
          let parsedSchema: JSONSchema6 = JSON.parse(schema);
          actualSchema = parsedSchema;
          onUpdate(parsedSchema);
        }}
        data={schemaData}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  '@global': {
    body: {
      '& .ant-switch:not(.ant-switch-checked), & .ant-switch[aria-checked="false"]': {
        background: theme.palette.grey[400] + ' !important',
      },
      '& .ant-select-arrow-icon': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .json-schema-react-editor .icon-object': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .ace_scroller ': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .ace-tm .ace_gutter, \
          & .ace_print-margin': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${alpha(theme.palette.text.primary, 0.6)} !important`,
      },
      '& .ant-input-number-handler-wrap, \
        & .ant-input-number-handler-down, \
        & .ant-input-number-handler-up': {
        border: 'none !important',
        color: `${theme.palette.text.secondary} !important`,
        backgroundColor: `transparent !important`,
      },
      '& .ant-input-number-handler-wrap .ant-input-number-handler-up:hover, \
        & .ant-input-number-handler-wrap .ant-input-number-handler-down:hover, \
        & .ant-input-number-handler-wrap .ant-input-number-handler-up:active, \
        & .ant-input-number-handler-wrap .ant-input-number-handler-down:active': {
        backgroundColor: `${theme.palette.action.hover} !important`,
        color: `${theme.palette.primary.main} !important`,
        border: 'none !important',
      },
      '& .ace-tm .ace_gutter .ace_gutter-active-line, \
        & .ace-tm .ace_gutter .ace_gutter-active-line-number': {
        backgroundColor: `${theme.palette.action.selected} !important`,
        color: `${theme.palette.text.primary} !important`,
      },
      // Theme-aware border color for disabled Ant Design checkbox
      '& .ant-checkbox-input[disabled] + .ant-checkbox-inner': {
        borderColor: `${alpha(theme.palette.text.disabled, 0.2)} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      // Theme-aware color for Ant Design edit icon
      '& .anticon-edit': {
        color: `${theme.palette.primary.main} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .anticon-setting': {
        color: `${theme.palette.text.secondary} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .anticon-plus': {
        color: `${theme.palette.text.secondary} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .anticon-close': {
        color: `${theme.palette.text.secondary} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .ant-modal-content': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${theme.palette.text.primary} !important`,
      },
      '& .ant-modal-header': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${theme.palette.text.primary} !important`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)} !important`,
      },
      '& .ant-modal-title': {
        color: `${theme.palette.text.primary} !important`,
      },
      '& .ant-modal-footer': {
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)} !important`,
      },
      [`& .ant-select-dropdown-menu-item:hover:not(.ant-select-dropdown-menu-item-disabled),
        .ant-select-dropdown-menu-item-selected,
        .ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled),
        .ant-dropdown-menu-item:hover, .ant-dropdown-menu-submenu-title:hover`]: {
        backgroundColor: alpha(theme.palette.primary.light, 0.2),
      },
      '& .ant-select-dropdown-menu-item-selected': {
        color: theme.palette.text.primary,
      },
      '& .ant-input:hover:not([disabled]), .ant-input:active:not([disabled]), .ant-input:focus:not([disabled])':
        {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.light, 0.2)}`,
        },
      '& .col-item-mock .ant-input, .col-item-desc .ant-input': {
        borderRight: 'none',
      },
      '& .ant-modal-close-x': {
        color: `${theme.palette.text.disabled} !important`,
      },
      '& .ant-btn': {
        borderColor: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
      },
      '& .ant-btn:hover': {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
      },
      '& .ant-btn.ant-btn-primary': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: theme.palette.secondary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          borderColor: theme.palette.primary.main,
          color: theme.palette.secondary.contrastText,
        },
      },
      '& .json-schema-react-editor, .ant-select-dropdown-menu, .ant-dropdown-menu': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      },
      '& .ant-input, & .ant-select-selection, & .ant-select-selector, & .ant-input-group-addon': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: theme.palette.text.primary,
        borderColor: `${theme.palette.text.secondary} !important`,
      },
      '& .ant-input[disabled], & .ant-select-disabled .ant-select-selector': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${alpha(theme.palette.text.disabled, 0.4)} !important`,
      },
      '& .ant-select-dropdown-menu-item, & .ant-dropdown-menu-item': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      },
    },
  },
  schemaEditorContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'auto',
    flex: 1,
    '& .json-schema-react-editor': {
      padding: '40px 0 40px 25px',

      '& .wrapper': {
        paddingLeft: 0,
      },
      '& .ant-col.object-style:first-child': {
        paddingTop: 0,
        marginTop: 0,
      },
      '& .ant-col-8': {
        width: '25.3333333%',
      },
      '& .ant-col-3': {
        width: '16.5%',
      },
      '& .ant-col-3.col-item-setting': {
        width: '9.5%',
      },
      '& .ant-col-5': {
        width: '24.3333%',
      },
      [`& .ant-select-selection:hover,
        .ant-select-selection:focus,
        .ant-select-selection:active,
        .ant-select-auto-complete.ant-select .ant-input:hover,
        .ant-input:hover,
        .ant-input:focus,
        .ant-checkbox-checked::after,
        `]: {
        borderColor: theme.palette.text.secondary,
      },
      [`& .ant-input:focus,
        .ant-select-selection:focus`]: {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.light, 0.2)}`,
      },
      '& .ant-input-group-addon': {
        color: `${theme.palette.text.secondary} !important`,
      },
      '& .ant-checkbox-inner': {
        borderColor: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.default,
      },
      [`& .ant-checkbox-wrapper:hover .ant-checkbox-inner,
        .ant-checkbox:hover .ant-checkbox-inner,
        .ant-checkbox-input:focus + .ant-checkbox-inner`]: {
        borderColor: theme.palette.primary.main,
      },
      '& .ant-checkbox-checked .ant-checkbox-inner': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      },
      '& .ant-input-wrapper.ant-input-group .ant-input-group-addon': {
        padding: '0 10px 0 20px',
      },
    },
  },
}));

export default SchemaEditor;
